import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Play, Lock, Scan, FileBarChart } from 'lucide-react';
import { useInventario, useItensInventario, useConferirItem, useIniciarInventario } from '../hooks';
import { InventoryStatusBadge, InventoryResultBadge, INVENTORY_RESULT_LABELS } from '../components/InventoryStatusBadge';
import { InventoryQuickCheckModal } from '../components/InventoryQuickCheckModal';
import { CheckDivergentModal } from '../components/CheckDivergentModal';
import { CloseInventoryModal } from '../components/CloseInventoryModal';
import { authService } from '../../../shared/lib/auth';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { InventoryItem, InventoryResult } from '../api';

export default function InventarioDetailPage() {
  const { id = '' } = useParams();
  const inv = useInventario(id);
  const user = authService.getCurrentUser();
  const { can } = usePermissions();
  const canManage = can(FEATURES.ativosInventario, 'edit');
  const [result, setResult] = useState<InventoryResult | ''>('');
  const [search, setSearch] = useState('');
  const items = useItensInventario(id, { result: result || undefined, search: search || undefined, limit: 500 });
  const conferir = useConferirItem(id);

  const [quickOpen, setQuickOpen] = useState(false);
  const [divergentItem, setDivergentItem] = useState<InventoryItem | null>(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const iniciar = useIniciarInventario();

  if (inv.isLoading) {
    return (
      <PageContainer title="Carregando…">
        <div className="h-64 animate-pulse rounded bg-gray-100" />
      </PageContainer>
    );
  }
  if (inv.isError || !inv.data) {
    return (
      <PageContainer title="Ação anual de inventário não encontrada">
        <Alert variant="error">Falha ao carregar a ação anual de inventário.</Alert>
      </PageContainer>
    );
  }

  const status = inv.data.inventory.status;
  const canWrite = canManage && status === 'in_progress';

  async function quickMark(item: InventoryItem, r: InventoryResult) {
    try {
      await conferir.mutateAsync({ itemId: item.id, payload: { result: r } });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setBanner(typeof msg === 'string' ? msg : 'Falha ao registrar');
    }
  }

  const progress = inv.data.inventory.progress;
  const done = progress.found + progress.not_found + progress.divergent + progress.damaged;
  const pct = progress.total > 0 ? Math.round((done / progress.total) * 100) : 0;

  return (
    <PageContainer
      title={inv.data.inventory.name}
      breadcrumbs={[
        { label: 'Ativos', to: '/ativos' },
        { label: 'Inventário', to: '/ativos/inventarios' },
        { label: inv.data.inventory.name },
      ]}
      action={
        <div className="flex flex-wrap justify-end gap-2">
          <InventoryStatusBadge status={status} />
          {canManage && status === 'planned' && (
            <Button
              size="sm"
              icon={<Play className="h-4 w-4" />}
              onClick={async () => { await iniciar.mutateAsync(id); setBanner('Ação anual de inventário iniciada.'); }}
            >
              Iniciar
            </Button>
          )}
          {canWrite && (
            <Button
              size="sm"
              icon={<Scan className="h-4 w-4" />}
              onClick={() => setQuickOpen(true)}
              className="!bg-purple-600 hover:!bg-purple-700 focus:!ring-purple-500"
            >
              Conferência rápida
            </Button>
          )}
          {canManage && status === 'in_progress' && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Lock className="h-4 w-4" />}
              onClick={() => setCloseOpen(true)}
              className="!border-red-300 !text-red-700 hover:!bg-red-50"
            >
              Fechar inventário
            </Button>
          )}
          <Link to={`/ativos/inventarios/${id}/relatorio`}>
            <Button variant="secondary" size="sm" icon={<FileBarChart className="h-4 w-4" />}>
              Relatório
            </Button>
          </Link>
        </div>
      }
    >
      {banner && (
        <Alert
          variant="info"
          action={<Button variant="ghost" size="sm" onClick={() => setBanner(null)}>fechar</Button>}
        >
          {banner}
        </Alert>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <p className="text-sm text-gray-700 mb-2">
          Progresso: <strong>{done}</strong> / {progress.total} ({pct}%)
        </p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div className={`h-full ${pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 flex gap-4 text-xs text-gray-700">
          <span>Localizados: <strong className="text-green-700">{progress.found}</strong></span>
          <span>Divergentes: <strong className="text-orange-700">{progress.divergent}</strong></span>
          <span>Não localizados: <strong className="text-red-700">{progress.not_found}</strong></span>
          <span>Danificados: <strong className="text-red-700">{progress.damaged}</strong></span>
          <span>Pendentes: <strong>{progress.pending}</strong></span>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-[200px]">
            <AutocompleteSelect
              label="Resultado"
              options={[
                { value: '', label: 'Todos os resultados' },
                ...(Object.entries(INVENTORY_RESULT_LABELS) as Array<[InventoryResult, string]>).map(([v, l]) => ({ value: v, label: l })),
              ]}
              value={result}
              onChange={(v) => setResult(v as InventoryResult | '')}
            />
          </div>
          <div className="flex-1 min-w-0 max-w-sm">
            <TextInput
              label="Buscar"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Código</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-left">Responsável esperado</th>
                <th className="px-4 py-2 text-left">Localização esperada</th>
                <th className="px-4 py-2 text-left">Resultado</th>
                {canWrite && <th className="px-4 py-2 text-right">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.data?.data.map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2 font-mono text-gray-800">{it.asset?.displayCode ?? it.asset?.assetCode}</td>
                  <td className="px-4 py-2 text-gray-700">{it.asset?.description}</td>
                  <td className="px-4 py-2 text-gray-700">{it.expectedCustodian?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-gray-700">{it.expectedLocation?.name ?? '—'}</td>
                  <td className="px-4 py-2"><InventoryResultBadge result={it.result} /></td>
                  {canWrite && (
                    <td className="px-4 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => quickMark(it, 'found')}
                          className="!border-green-300 !text-green-700 hover:!bg-green-50"
                        >
                          Localizado
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            const damageNotes = window.prompt('Descreva o dano identificado:');
                            if (damageNotes?.trim()) {
                              void conferir.mutateAsync({
                                itemId: it.id,
                                payload: { result: 'damaged', damageNotes: damageNotes.trim() },
                              }).catch(() => setBanner('Não foi possível registrar o dano identificado.'));
                            }
                          }}
                          className="!border-red-300 !text-red-700 hover:!bg-red-50"
                        >
                          Danificado
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setDivergentItem(it)}
                          className="!border-orange-300 !text-orange-700 hover:!bg-orange-50"
                        >
                          Divergente
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => quickMark(it, 'not_found')}
                          className="!border-red-300 !text-red-700 hover:!bg-red-50"
                        >
                          Não localizado
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {items.data && items.data.data.length === 0 && (
                <tr>
                  <td colSpan={canWrite ? 6 : 5} className="p-0">
                    <EmptyState title="Nenhum item para conferir" size="compact" />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <InventoryQuickCheckModal open={quickOpen} inventoryId={id} onClose={() => setQuickOpen(false)} />
      <CheckDivergentModal open={!!divergentItem} inventoryId={id} item={divergentItem}
        onClose={() => setDivergentItem(null)} onSuccess={() => setBanner('Divergência registrada.')} />
      <CloseInventoryModal open={closeOpen} inventoryId={id} onClose={() => setCloseOpen(false)}
        onClosed={(s) => setBanner(`Inventário fechado — ${s.foundCount} localizados, ${s.notFoundCount} não localizados, ${s.divergentCount} divergências. Alertas gerados: ${s.alertsCreated}.`)} />
    </PageContainer>
  );
}
